import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Power, Save, X, RotateCcw, Moon } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import AccountChannelsPanel from './AccountChannelsPanel';

const CATEGORIES = ['ביטחוני', 'פוליטיקה', 'נעייס', ''];

// הלחצנים של כל ערוץ. strict=true: שדות שיכולים להיות null - דולק רק כשהערך בדיוק true
const TOGGLES = [
  { key: 'publish_to_general', label: 'כללי', color: 'blue', title: 'פרסום באתר הכללי' },
  { key: 'publish_to_phone', label: 'טלפון', color: 'green', title: 'פרסום בקו הטלפוני' },
  { key: 'publish_to_personal', label: 'אישי', color: 'purple', title: 'שליחה ל-API אישי' },
  { key: 'send_to_admin_only', label: 'מנהלים', icon: '🔒', color: 'orange', strict: true,
    title: 'שליחה למנהלים באתר (גם אם אישי כבוי)' },
  { key: 'send_videos_to_personal', label: 'וידאו לאישי', icon: '🎬', color: 'pink', strict: true,
    title: 'שליחת סרטונים ל-API אישי' },
];

const TOGGLE_COLORS = {
  blue: 'bg-blue-500 hover:bg-blue-600',
  green: 'bg-green-500 hover:bg-green-600',
  purple: 'bg-purple-500 hover:bg-purple-600',
  orange: 'bg-orange-500 hover:bg-orange-600',
  pink: 'bg-pink-500 hover:bg-pink-600',
  teal: 'bg-teal-500 hover:bg-teal-600',
};

// העמודות שיש להן פעולות מרוכזות (איפוס/החזרה + מצב שבת)
const COLUMN_GROUPS = [
  { field: 'publish_to_general', label: 'כללי', shabbat: 'shabbat_mode_general', btn: 'bg-blue-100 hover:bg-blue-200 text-blue-600' },
  { field: 'publish_to_phone', label: 'טלפון', shabbat: 'shabbat_mode_phone', btn: 'bg-green-100 hover:bg-green-200 text-green-600' },
  { field: 'publish_to_personal', label: 'אישי', shabbat: 'shabbat_mode_personal', btn: 'bg-purple-100 hover:bg-purple-200 text-purple-600' },
];

const CheckIcon = () => (
  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const isOn = (channel, t) => (t.strict ? channel[t.key] === true : !!channel[t.key]);

function ToggleButton({ channel, toggle, onChange }) {
  const on = isOn(channel, toggle);
  return (
    <button
      onClick={() => onChange(channel.id, toggle.key, !channel[toggle.key])}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
        on ? TOGGLE_COLORS[toggle.color] : 'bg-gray-200 hover:bg-gray-300'
      }`}
      title={toggle.title}
    >
      {on ? <CheckIcon /> : null}
    </button>
  );
}

export default function ChannelsTab() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    category: '',
    enabled: true,
    publish_to_general: true,
    publish_to_phone: true,
    publish_to_personal: true,
    send_videos_to_personal: true,
    send_to_admin_only: true
  });
  
  // State לשמירת מצב קודם של טורים (לפונקציית החזרה)
  const [previousColumnState, setPreviousColumnState] = useState({
    general: {},
    phone: {},
    personal: {}
  });
  
  // Ref לזיהוי לחיצה כפולה
  const clickTimeoutRef = useRef(null);
  const clickCountRef = useRef({});

  useEffect(() => {
    loadChannels();
  }, []);

  const loadChannels = async () => {
    try {
      const response = await api.get('/channels');
      setChannels(response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת ערוצים');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    
    // בדיקת ערוץ כפול
    const cleanUsername = formData.username.replace('@', '').toLowerCase();
    const existingChannel = channels.find(c => 
      c.username.toLowerCase() === cleanUsername
    );
    
    if (existingChannel) {
      toast.error(`ערוץ @${cleanUsername} כבר קיים במערכת`);
      return;
    }
    
    try {
      await api.post('/channels', formData);
      toast.success('ערוץ נוסף בהצלחה');
      setFormData({ username: '', name: '', category: '', enabled: true, publish_to_general: true, publish_to_phone: true, publish_to_personal: true, send_videos_to_personal: true, send_to_admin_only: true });
      setShowAddForm(false);
      loadChannels();
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בהוספת ערוץ');
    }
  };

  const handleUpdate = async (id) => {
    const channel = channels.find(c => c.id === id);
    try {
      await api.put(`/channels/${id}`, {
        username: channel.username,
        name: channel.name,
        category: channel.category || '',
        enabled: channel.enabled ? 1 : 0
      });
      toast.success('ערוץ עודכן בהצלחה');
      setEditingId(null);
      loadChannels();
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בעדכון ערוץ');
      console.error('Update error:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק ערוץ זה?')) {
      return;
    }
    
    try {
      await api.delete(`/channels/${id}`);
      toast.success('ערוץ נמחק בהצלחה');
      loadChannels();
    } catch (error) {
      toast.error('שגיאה במחיקת ערוץ');
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.patch(`/channels/${id}/toggle`);
      toast.success('מצב הערוץ השתנה');
      loadChannels();
    } catch (error) {
      toast.error('שגיאה בשינוי מצב');
    }
  };

  const updateChannel = async (id, field, value) => {
    // קבלת הערוץ הנוכחי לפני העדכון
    const channel = channels.find(c => c.id === id);
    
    if (!channel) {
      toast.error('ערוץ לא נמצא');
      return;
    }
    
    // עדכון מיידי ב-state
    setChannels(channels.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
    
    // שמירה אוטומטית בשרת
    try {
      const updateData = {
        username: field === 'username' ? value : (channel.username || ''),
        name: field === 'name' ? value : (channel.name || ''),
        category: field === 'category' ? (value || '') : (channel.category || ''),
        enabled: field === 'enabled' ? (value ? 1 : 0) : (channel.enabled ? 1 : 0),
        publish_to_general: field === 'publish_to_general' ? (value ? 1 : 0) : (channel.publish_to_general ? 1 : 0),
        publish_to_phone: field === 'publish_to_phone' ? (value ? 1 : 0) : (channel.publish_to_phone ? 1 : 0),
        publish_to_personal: field === 'publish_to_personal' ? (value ? 1 : 0) : (channel.publish_to_personal ? 1 : 0),
        send_videos_to_personal: field === 'send_videos_to_personal' ? (value ? 1 : 0) : (channel.send_videos_to_personal ? 1 : 0),
        send_to_admin_only: field === 'send_to_admin_only' ? (value ? 1 : 0) : (channel.send_to_admin_only ? 1 : 0),
        shabbat_mode_general: field === 'shabbat_mode_general' ? (value ? 1 : 0) : (channel.shabbat_mode_general ? 1 : 0),
        shabbat_mode_phone: field === 'shabbat_mode_phone' ? (value ? 1 : 0) : (channel.shabbat_mode_phone ? 1 : 0),
        shabbat_mode_personal: field === 'shabbat_mode_personal' ? (value ? 1 : 0) : (channel.shabbat_mode_personal ? 1 : 0)
      };
      
      console.log('Updating channel:', id, 'field:', field, 'value:', value, 'updateData:', updateData);
      
      await api.put(`/channels/${id}`, updateData);
      toast.success('עודכן בהצלחה', { duration: 1000 });
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בעדכון');
      console.error('Update error:', error.response?.data || error);
      // החזרת המצב הקודם במקרה של שגיאה
      loadChannels();
    }
  };

  // פונקציה לטיפול בלחיצה על כפתור טור (לחיצה רגילה/כפולה)
  const handleColumnButtonClick = (columnField, columnName) => {
    const columnKey = columnField.replace('publish_to_', '');
    
    // בדיקה אם זו לחיצה כפולה
    if (!clickCountRef.current[columnField]) {
      clickCountRef.current[columnField] = 0;
    }
    clickCountRef.current[columnField]++;
    
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    clickTimeoutRef.current = setTimeout(async () => {
      const clickCount = clickCountRef.current[columnField];
      clickCountRef.current[columnField] = 0;
      
      if (clickCount >= 2) {
        // לחיצה כפולה - סימון הכל
        await toggleColumnAll(columnField, true);
        toast.success(`כל הערוצים סומנו ב${columnName}`);
      } else {
        // לחיצה רגילה - איפוס או החזרה
        await toggleColumnReset(columnField, columnKey, columnName);
      }
    }, 300);
  };

  // איפוס טור או החזרה למצב קודם
  const toggleColumnReset = async (columnField, columnKey, columnName) => {
    // בדיקה אם יש מצב קודם שמור
    const hasPreviousState = Object.keys(previousColumnState[columnKey]).length > 0;
    
    // שמירת המצב הנוכחי לפני השינוי
    const currentState = {};
    channels.forEach(c => {
      currentState[c.id] = c[columnField];
    });
    
    // בדיקה האם הטור כולו כבוי
    const allOff = channels.every(c => !c[columnField]);
    
    if (allOff && hasPreviousState) {
      // החזרה למצב הקודם
      const updates = channels.map(c => ({
        ...c,
        [columnField]: previousColumnState[columnKey][c.id] || false
      }));
      setChannels(updates);
      
      // עדכון בשרת - שימוש ב-bulk endpoint
      const bulkUpdates = updates.map(channel => ({
        id: channel.id,
        [columnField]: previousColumnState[columnKey][channel.id] !== undefined ? 
          previousColumnState[columnKey][channel.id] : false
      }));
      
      try {
        await api.put('/channels/bulk', { channels: bulkUpdates });
        toast.success(`הוחזר המצב הקודם של ${columnName}`);
      } catch (error) {
        toast.error('שגיאה בעדכון המוני');
        console.error('Bulk update error:', error);
      }
      
      setPreviousColumnState(prev => ({ ...prev, [columnKey]: {} }));
    } else {
      // שמירת המצב הנוכחי וכיבוי הכל
      setPreviousColumnState(prev => ({ ...prev, [columnKey]: currentState }));
      
      const updates = channels.map(c => ({
        ...c,
        [columnField]: false
      }));
      setChannels(updates);
      
      // עדכון בשרת - שימוש ב-bulk endpoint
      const bulkUpdates = channels.map(channel => ({
        id: channel.id,
        [columnField]: false
      }));
      
      try {
        await api.put('/channels/bulk', { channels: bulkUpdates });
        toast.success(`כל הערוצים בוטלו מ${columnName}`);
      } catch (error) {
        toast.error('שגיאה בעדכון המוני');
        console.error('Bulk update error:', error);
      }
    }
  };

  // סימון כל הטור
  const toggleColumnAll = async (columnField, value) => {
    const updates = channels.map(c => ({
      ...c,
      [columnField]: value
    }));
    setChannels(updates);
    
    // עדכון בשרת - שימוש ב-bulk endpoint
    const bulkUpdates = channels.map(channel => ({
      id: channel.id,
      [columnField]: value
    }));
    
    try {
      await api.put('/channels/bulk', { channels: bulkUpdates });
    } catch (error) {
      console.error('Bulk update error:', error);
    }
  };

  // עדכון ערוץ ללא הודעת toast
  const updateChannelSilent = async (id, field, value) => {
    const channel = channels.find(c => c.id === id);
    if (!channel) return;
    
    try {
      const updateData = {
        username: channel.username || '',
        name: channel.name || '',
        category: channel.category || '',
        enabled: channel.enabled ? 1 : 0,
        publish_to_general: field === 'publish_to_general' ? (value ? 1 : 0) : (channel.publish_to_general ? 1 : 0),
        publish_to_phone: field === 'publish_to_phone' ? (value ? 1 : 0) : (channel.publish_to_phone ? 1 : 0),
        publish_to_personal: field === 'publish_to_personal' ? (value ? 1 : 0) : (channel.publish_to_personal ? 1 : 0),
        send_videos_to_personal: channel.send_videos_to_personal ? 1 : 0,
        send_to_admin_only: channel.send_to_admin_only ? 1 : 0,
        shabbat_mode_general: field === 'shabbat_mode_general' ? (value ? 1 : 0) : (channel.shabbat_mode_general ? 1 : 0),
        shabbat_mode_phone: field === 'shabbat_mode_phone' ? (value ? 1 : 0) : (channel.shabbat_mode_phone ? 1 : 0),
        shabbat_mode_personal: field === 'shabbat_mode_personal' ? (value ? 1 : 0) : (channel.shabbat_mode_personal ? 1 : 0)
      };
      
      await api.put(`/channels/${id}`, updateData);
    } catch (error) {
      console.error('Silent update error:', error);
    }
  };

  // החלפת מצב שבת לטור שלם
  const toggleShabbatMode = async (shabbatField, columnName) => {
    // בדיקה אם כולם כבר במצב שבת
    const allShabbat = channels.every(c => c[shabbatField]);
    const newValue = !allShabbat;
    
    const updates = channels.map(c => ({
      ...c,
      [shabbatField]: newValue
    }));
    setChannels(updates);
    
    // עדכון בשרת - שימוש ב-bulk endpoint
    const bulkUpdates = channels.map(channel => ({
      id: channel.id,
      [shabbatField]: newValue
    }));
    
    try {
      await api.put('/channels/bulk', { channels: bulkUpdates });
      toast.success(newValue ? `מצב שבת הופעל ל${columnName}` : `מצב שבת בוטל מ${columnName}`);
    } catch (error) {
      toast.error('שגיאה בעדכון מצב שבת');
      console.error('Shabbat mode bulk update error:', error);
    }
  };

  if (loading) {
    return <div className="text-center py-12">טוען ערוצים...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">ניהול ערוצים</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5 ml-2" />
          הוסף ערוץ
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              type="text"
              placeholder="Username (@)"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
            <input
              type="text"
              placeholder="שם הערוץ"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat || 'ללא קטגוריה'}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                שמור
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* מקרא - מה כל לחצן עושה */}
      <details className="bg-white rounded-lg shadow border border-gray-200">
        <summary className="px-4 py-3 text-sm font-medium text-gray-700 cursor-pointer">
          ℹ️ מה כל לחצן עושה?
        </summary>
        <ul className="px-4 pb-4 space-y-1 text-sm text-gray-600">
          {TOGGLES.map(t => (
            <li key={t.key}><strong>{t.icon ? `${t.icon} ` : ''}{t.label}</strong> - {t.title}</li>
          ))}
        </ul>
      </details>

      {/* ערוצים שהחשבון כבר חבר בהם ולא במעקב - נפתח בלחיצה */}
      <AccountChannelsPanel onAdded={loadChannels} toggles={TOGGLES} />

      {/* פעולות מרוכזות לכל העמודות - במובייל (בדסקטופ הן בכותרות הטבלה) */}
      <div className="md:hidden bg-white rounded-lg shadow border border-gray-200 p-3 space-y-2">
        <div className="text-xs font-medium text-gray-500">פעולות על כל הערוצים</div>
        {COLUMN_GROUPS.map(g => (
          <div key={g.field} className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{g.label}</span>
            <div className="flex gap-2">
              <button
                onClick={() => handleColumnButtonClick(g.field, g.label)}
                className={`p-2 rounded ${g.btn}`}
                title="לחיצה: איפוס/החזרה | לחיצה כפולה: סימון הכל"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                onClick={() => toggleShabbatMode(g.shabbat, g.label)}
                className={`p-2 rounded ${
                  channels.some(c => c[g.shabbat]) ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}
                title="מצב שבת - לא יעלו הודעות בשבת"
              >
                <Moon className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Channels List - מובייל: כרטיסים */}
      <div className="md:hidden space-y-3">
        {channels.map((channel) => (
          <div key={channel.id} className="bg-white rounded-lg shadow border border-gray-200 p-4 space-y-3">
            {editingId === channel.id ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={channel.username}
                  onChange={(e) => updateChannel(channel.id, 'username', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Username"
                />
                <input
                  type="text"
                  value={channel.name}
                  onChange={(e) => updateChannel(channel.id, 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="שם הערוץ"
                />
                <select
                  value={channel.category}
                  onChange={(e) => updateChannel(channel.id, 'category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat || 'ללא'}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 break-words">{channel.name}</div>
                  <div className="text-xs text-gray-500 break-all" dir="ltr">@{channel.username}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className={`px-2 text-xs leading-5 font-semibold rounded-full ${
                    channel.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {channel.enabled ? 'פעיל' : 'כבוי'}
                  </span>
                  <span className="px-2 text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {channel.category || 'ללא'}
                  </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              {TOGGLES.map(t => (
                <div key={t.key} className="flex flex-col items-center gap-1 py-1">
                  <ToggleButton channel={channel} toggle={t} onChange={updateChannel} />
                  <span className="text-[11px] text-gray-600 text-center leading-tight">
                    {t.icon ? `${t.icon} ` : ''}{t.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex gap-4 justify-end border-t border-gray-100 pt-3">
              {editingId === channel.id ? (
                <>
                  <button onClick={() => handleUpdate(channel.id)} className="p-1 text-green-600"><Save className="w-6 h-6" /></button>
                  <button onClick={() => setEditingId(null)} className="p-1 text-gray-600"><X className="w-6 h-6" /></button>
                </>
              ) : (
                <>
                  <button onClick={() => setEditingId(channel.id)} className="p-1 text-blue-600"><Edit2 className="w-6 h-6" /></button>
                  <button onClick={() => handleToggle(channel.id)} className="p-1 text-yellow-600"><Power className="w-6 h-6" /></button>
                  <button onClick={() => handleDelete(channel.id)} className="p-1 text-red-600"><Trash2 className="w-6 h-6" /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Channels List - דסקטופ: טבלה צפופה שנכנסת בשורה */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">שם</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">קטגוריה</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מצב</th>
              {TOGGLES.map(t => {
                const g = COLUMN_GROUPS.find(x => x.field === t.key);
                return (
                  <th key={t.key} className="px-2 py-3 text-center text-xs font-medium text-gray-500 tracking-wider" title={t.title}>
                    <div className="flex flex-col items-center gap-1">
                      {t.icon && <span className="text-base leading-none">{t.icon}</span>}
                      <span className="whitespace-nowrap">{t.label}</span>
                      {g && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleColumnButtonClick(g.field, g.label)}
                            className={`p-1 rounded transition-colors ${g.btn}`}
                            title="לחיצה: איפוס/החזרה | לחיצה כפולה: סימון הכל"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => toggleShabbatMode(g.shabbat, g.label)}
                            className={`p-1 rounded transition-colors ${
                              channels.some(c => c[g.shabbat])
                                ? 'bg-purple-500 text-white'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                            }`}
                            title="מצב שבת - לא יעלו הודעות בשבת"
                          >
                            <Moon className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">פעולות</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {channels.map((channel) => (
              <tr key={channel.id} className="hover:bg-gray-50">
                <td className="px-3 py-3 max-w-[160px]">
                  {editingId === channel.id ? (
                    <input
                      type="text"
                      value={channel.username}
                      onChange={(e) => updateChannel(channel.id, 'username', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  ) : (
                    <span className="text-sm text-gray-900 block truncate" dir="ltr" title={`@${channel.username}`}>@{channel.username}</span>
                  )}
                </td>
                <td className="px-3 py-3 max-w-[200px]">
                  {editingId === channel.id ? (
                    <input
                      type="text"
                      value={channel.name}
                      onChange={(e) => updateChannel(channel.id, 'name', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  ) : (
                    <span className="text-sm font-medium text-gray-900 block truncate" title={channel.name}>{channel.name}</span>
                  )}
                </td>
                <td className="px-3 py-3 whitespace-nowrap">
                  {editingId === channel.id ? (
                    <select
                      value={channel.category}
                      onChange={(e) => updateChannel(channel.id, 'category', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat || 'ללא'}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {channel.category || 'ללא'}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    channel.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {channel.enabled ? 'פעיל' : 'כבוי'}
                  </span>
                </td>
                {TOGGLES.map(t => (
                  <td key={t.key} className="px-2 py-3 whitespace-nowrap text-center">
                    <ToggleButton channel={channel} toggle={t} onChange={updateChannel} />
                  </td>
                ))}
                <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    {editingId === channel.id ? (
                      <>
                        <button onClick={() => handleUpdate(channel.id)} className="text-green-600 hover:text-green-900"><Save className="w-5 h-5" /></button>
                        <button onClick={() => setEditingId(null)} className="text-gray-600 hover:text-gray-900"><X className="w-5 h-5" /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditingId(channel.id)} className="text-blue-600 hover:text-blue-900"><Edit2 className="w-5 h-5" /></button>
                        <button onClick={() => handleToggle(channel.id)} className="text-yellow-600 hover:text-yellow-900"><Power className="w-5 h-5" /></button>
                        <button onClick={() => handleDelete(channel.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-5 h-5" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
